import { newSmallStepContent } from 'common/src/SchemeOfLearning/SmallStep';
import { newQuestionContent } from '../../../Question';
import { z } from 'zod';
import {
  getRandomBoolean,
  getRandomFromArray,
  randomIntegerInclusive,
  randomIntegerInclusiveStep,
  randomUniqueIntegersInclusive,
  randomUniqueIntegersInclusiveStep,
  rejectionSample,
  shuffle
} from '../../../../utils/random';
import {
  displayMoney,
  moneyPenceToString,
  moneyScaledSizes,
  moneyStringToPence,
  moneyToHighestDenominations,
  penceToPoundsAndPence,
  possibleMoneyDenominations,
  PossibleMoneyDenominations,
  PossibleMoneyStringDenominations,
  totalPenceToPoundsAndPence
} from '../../../../utils/money';
import { getSumCombinations, lessThanGreaterThanOrEqualTo } from '../../../../utils/math';
import QF6DragMatchStatements from '../../../../components/question/questionFormats/QF6DragMatchStatements';
import Text from 'common/src/components/typography/Text';
import { View } from 'react-native';
import QF11SelectImagesUpTo4 from '../../../../components/question/questionFormats/QF11SelectImagesUpTo4';
import {
  arrayHasNoDuplicates,
  arraysHaveSameContentsUnordered,
  sumNumberArray
} from '../../../../utils/collections';
import { numberEnum } from '../../../../utils/zod';

////
// Questions
////

const Question1 = newQuestionContent({
  uid: 'bio',
  description: 'bio',
  keywords: [
    'Count',
    'Coins',
    'Greater than',
    'Less than',
    'Equal to',
    'Compare',
    'Pounds',
    'Notes',
    'Coins'
  ],
  schema: z.object({
    moneyInPenceA: z.number().int().min(1).max(2999),
    moneyInPenceB: z.number().int().min(1).max(2999)
  }),
  questionHeight: 1200,
  simpleGenerator: () => {
    const variation = getRandomFromArray([
      'bothAmountsPounds',
      'bothAmountsPence',
      'oneAmountPoundsOnePence',
      'bothAmountPoundsAndPence'
    ] as const);

    const [moneyInPenceA, moneyInPenceB] = (() => {
      switch (variation) {
        case 'bothAmountsPounds':
          return randomUniqueIntegersInclusiveStep(100, 2900, 100, 2);
        case 'bothAmountsPence':
          return randomUniqueIntegersInclusive(1, 99, 2);
        case 'oneAmountPoundsOnePence':
          return shuffle([
            randomIntegerInclusiveStep(100, 2900, 100),
            randomIntegerInclusive(1, 99)
          ]);
        case 'bothAmountPoundsAndPence':
          return randomUniqueIntegersInclusive(1, 2999, 2, { constraint: x => x % 100 !== 0 });
      }
    })();

    return { moneyInPenceA, moneyInPenceB };
  },
  Component: ({ question: { moneyInPenceA, moneyInPenceB }, translate, displayMode }) => {
    const moneyA = totalPenceToPoundsAndPence(moneyInPenceA);
    const moneyB = totalPenceToPoundsAndPence(moneyInPenceB);

    const money = displayMoney(
      [...moneyA, ...moneyB],
      displayMode === 'digital' ? 90 : 130,
      displayMode === 'digital' ? 90 : 130,
      true,
      true
    );

    const [moneyAScaled, moneyBScaled] = [
      money.filter((_, index) => index <= moneyA.length - 1),
      money.filter((_, index) => index >= moneyA.length)
    ];

    return (
      <QF6DragMatchStatements
        title={translate.ks1Instructions.dragACardToCompareTheAmounts()}
        pdfTitle={translate.ks1PDFInstructions.writeLessThanGreaterThanOrEqualSymbolsToCompareTheAmounts()}
        itemVariant="square"
        statements={[
          {
            lhsComponent: (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  width: displayMode === 'digital' ? 400 : 800,
                  gap: 5
                }}
              >
                {moneyAScaled}
              </View>
            ),
            rhsComponent: (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  width: displayMode === 'digital' ? 400 : 600,
                  gap: 5
                }}
              >
                {moneyBScaled}
              </View>
            ),
            correctAnswer: lessThanGreaterThanOrEqualTo(moneyInPenceA, moneyInPenceB)
          }
        ]}
        mainPanelStyle={{ alignItems: 'center' }}
        items={['>', '<', '=']}
        moveOrCopy="move"
        questionHeight={1200}
        actionPanelVariant="end"
        pdfLayout="itemsHidden"
      />
    );
  }
});

const Question2 = newQuestionContent({
  uid: 'bip',
  description: 'bip',
  keywords: ['Greatest', 'Smallest', 'Money', 'Pounds', 'Pence', 'Coins', 'Notes'],
  schema: z
    .object({
      selectables: z.array(
        z.object({ value: z.number().int().min(1).max(2999), denominations: z.array(z.string()) })
      ),
      isGreatest: z.boolean()
    })
    .refine(
      val => arrayHasNoDuplicates(val.selectables.map(({ value }) => value)),
      'There should be no duplicate values in selectables'
    ),
  simpleGenerator: () => {
    const isGreatest = getRandomBoolean();

    const variation = getRandomFromArray([
      'allAmountsPounds',
      'allAmountsPence',
      'allAmountsPoundsAndPence'
    ] as const);

    const selectables = rejectionSample(
      () => {
        const pence = (() => {
          switch (variation) {
            case 'allAmountsPence': {
              return randomUniqueIntegersInclusive(1, 99, 4);
            }
            case 'allAmountsPounds': {
              return randomUniqueIntegersInclusiveStep(100, 2999, 100, 4);
            }
            case 'allAmountsPoundsAndPence': {
              return randomUniqueIntegersInclusive(101, 2999, 4, {
                constraint: x => x % 100 !== 0
              });
            }
          }
        })();

        const selectables = pence.map(amount => {
          const pounds = Math.floor(amount / 100);
          const pence = amount % 100;

          const amountPence = moneyToHighestDenominations(pence, 'pence');
          const amountPounds = moneyToHighestDenominations(pounds, 'pounds');

          return { value: amount, denominations: [...amountPounds, ...amountPence] };
        });

        return selectables;
      },
      selectables => selectables.every(array => array.denominations.length <= 6)
    );

    return { selectables, isGreatest };
  },
  Component: props => {
    const {
      question: { selectables, isGreatest },
      translate,
      displayMode
    } = props;

    const flattenedMoneyArray = selectables.map(selectable => selectable.denominations).flat();

    // We need to get moneyScales here, so we can use a uniform scale across money in both the items and statements:
    const moneyScales = moneyScaledSizes(flattenedMoneyArray);

    const baseSize = displayMode === 'digital' ? 80 : 100;

    return (
      <QF11SelectImagesUpTo4
        title={
          isGreatest
            ? translate.ks1Instructions.selectTheGreatestAmountOfMoney()
            : translate.ks1Instructions.selectTheSmallestAmountOfMoney()
        }
        pdfTitle={
          isGreatest
            ? translate.ks1PDFInstructions.tickTheGreatestAmountOfMoney()
            : translate.ks1PDFInstructions.tickTheSmallestAmountOfMoney()
        }
        testCorrect={[
          isGreatest
            ? Math.max(...selectables.map(({ value }) => value))
            : Math.min(...selectables.map(({ value }) => value))
        ]}
        numItems={4}
        renderItems={selectables.map(({ value, denominations }) => {
          return {
            value: value,
            component: (
              <View
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  columnGap: 12,
                  rowGap: 12
                }}
              >
                {denominations.map(denom => {
                  return displayMoney(
                    [denom],
                    baseSize * moneyScales[denom],
                    baseSize * moneyScales[denom]
                  );
                })}
              </View>
            )
          };
        })}
      />
    );
  }
});

const Question2v2 = newQuestionContent({
  uid: 'bip2',
  description: 'bip',
  keywords: ['Greatest', 'Smallest', 'Money', 'Pounds', 'Pence', 'Coins', 'Notes'],
  schema: z
    .object({
      selectables: z.array(
        z.object({
          value: z.number().int().min(1).max(2999),
          denominations: z.array(numberEnum(possibleMoneyDenominations))
        })
      ),
      isGreatest: z.boolean()
    })
    .refine(
      val => arrayHasNoDuplicates(val.selectables.map(({ value }) => value)),
      'There should be no duplicate values in selectables'
    ),
  simpleGenerator: () => {
    const isGreatest = getRandomBoolean();
    const variant = getRandomFromArray(['pence', 'pounds', 'both'] as const);

    const pence = [1, 1, 1, 2, 2, 2, 5, 5, 5, 10, 10, 10, 20, 20, 20, 50, 50, 50] as const;
    const pounds = [
      100, 100, 100, 200, 200, 200, 500, 500, 500, 1000, 1000, 1000, 2000, 2000, 2000
    ] as const;

    const denominations =
      variant === 'pence'
        ? pence
        : variant === 'pounds'
        ? pounds
        : ([...pence, ...pounds] as const);
    let pence1: number, pence2: number, pence3: number, pence4: number;
    let pence1Denominations: PossibleMoneyDenominations[],
      pence2Denominations: PossibleMoneyDenominations[],
      pence3Denominations: PossibleMoneyDenominations[],
      pence4Denominations: PossibleMoneyDenominations[];

    if (variant === 'both') {
      let amountPounds: PossibleMoneyDenominations[];
      let amountPence: PossibleMoneyDenominations[];

      // generate pence1, this is the base set of money that they’re all based on
      ({ pence1, amountPounds, amountPence } = rejectionSample(
        () => {
          const pence1 = randomIntegerInclusive(203, 2898, { constraint: x => x % 100 !== 0 });

          const poundsOnly = Math.floor(pence1 / 100);
          const penceOnly = pence1 % 100;

          const amountPence = moneyToHighestDenominations(
            penceOnly,
            'pence'
          ) as PossibleMoneyStringDenominations[];
          const amountPounds = moneyToHighestDenominations(
            poundsOnly,
            'pounds'
          ) as PossibleMoneyStringDenominations[];

          return {
            pence1,
            amountPounds: amountPounds.map(val => moneyStringToPence[val]),
            amountPence: amountPence.map(val => moneyStringToPence[val])
          };
        },
        val =>
          [...val.amountPounds, ...val.amountPence].length <= 5 &&
          [...val.amountPounds, ...val.amountPence].length >= 2
      ));

      pence1Denominations = [...amountPounds, ...amountPence];

      // generate pence2, this is pence1 add/subtract a pound denomination
      ({ pence2, pence2Denominations } = rejectionSample(
        () => {
          const isAdd = getRandomBoolean();
          if (isAdd) {
            const addCoin = getRandomFromArray(
              [100, 200, 500, 1000, 2000].filter(val => val + pence1 < 3000)
            ) as PossibleMoneyDenominations;
            return {
              pence2: pence1 + addCoin,
              pence2Denominations: [...pence1Denominations, addCoin]
            };
          } else {
            const options = shuffle(pence1Denominations).filter(val => val >= 100);
            const removeId = pence1Denominations.findIndex(it => it === options[0]);
            const denom2 = pence1Denominations.filter((_, id) => id !== removeId);
            return {
              pence2: sumNumberArray(denom2),
              pence2Denominations: denom2
            };
          }
        },
        val => val.pence2 < 3000
      ));

      // generate pence3, this is pence1 add/subtract a pence denomination
      ({ pence3, pence3Denominations } = rejectionSample(
        () => {
          // do not want pence making a new pound
          const isAdd = pence1 % 100 !== 99 && getRandomBoolean();
          if (isAdd) {
            const addCoin = getRandomFromArray([
              ...[1, 2, 5, 10, 20, 50].filter(val => val + (pence1 % 100) < 100)
            ] as const) as PossibleMoneyDenominations;
            return {
              pence3: pence1 + addCoin,
              pence3Denominations: [...pence1Denominations, addCoin]
            };
          } else {
            const options = shuffle(pence1Denominations).filter(val => val < 100);
            const removeId = pence1Denominations.findIndex(it => it === options[0]);
            const denom3 = pence1Denominations.filter((_, id) => id !== removeId);
            return {
              pence3: sumNumberArray(denom3),
              pence3Denominations: denom3
            };
          }
        },
        val => val.pence3 < 3000
      ));

      // generate pence4, this is pence1 pounds with a difference arrangement and pence add/sub a coin or vice versa
      ({ pence4, pence4Denominations } = rejectionSample(
        () => {
          const isKeepPound = amountPounds.length === 1 || getRandomBoolean();

          // add or remove to either pounds or pence
          const isAdd = getRandomBoolean();
          let denom4: PossibleMoneyDenominations[];
          const options = isKeepPound ? amountPence : amountPounds;
          if (isAdd) {
            denom4 = [...options, getRandomFromArray(isKeepPound ? pence : pounds)];
          } else {
            const removeOptions = shuffle(options);
            denom4 = removeOptions.filter((_, id) => id > 0);
          }
          const other = isKeepPound ? amountPounds : amountPence;
          const denominationsNew = isKeepPound ? pounds : pence;
          const alternativeArrangement = getSumCombinations(
            shuffle(denominationsNew),
            sumNumberArray(other)
          ) as PossibleMoneyDenominations[];
          return {
            pence4: sumNumberArray([...denom4, ...alternativeArrangement]),
            pence4Denominations: [...denom4, ...alternativeArrangement],
            penceOnly: isKeepPound ? denom4 : alternativeArrangement
          };
        },
        val =>
          val.pence4 < 3000 &&
          ![pence1, pence2, pence3].includes(val.pence4) &&
          sumNumberArray(val.penceOnly) < 100 &&
          val.pence4Denominations.length <= 6
      ));
    } // else we generate only pounds or pence
    else {
      let pence1Denominations2: PossibleMoneyDenominations[];

      // generate pence1, this is the base set of money that they’re all based on
      ({ pence1, pence1Denominations, pence1Denominations2 } = rejectionSample(
        () => {
          const min = variant === 'pence' ? 4 : 300;
          const max = variant === 'pence' ? 98 : 2800;
          const notIncludesArray =
            variant === 'pence' ? [5, 10, 20, 50] : [100, 200, 500, 1000, 2000];
          const pence1 = randomIntegerInclusiveStep(
            min,
            max,
            variant === 'pounds' ? 100 : 1,
            // we want it to be made of at least 2 values so we can remove one
            { constraint: x => !notIncludesArray.includes(x) }
          );

          const pence1Denominations = getSumCombinations(
            shuffle(denominations),
            pence1
          ) as PossibleMoneyDenominations[];
          const pence1Denominations2 = getSumCombinations(
            shuffle(denominations),
            pence1
          ) as PossibleMoneyDenominations[];
          return { pence1, pence1Denominations, pence1Denominations2 };
        },
        val =>
          val.pence1Denominations.length <= 5 &&
          val.pence1Denominations.length >= 2 &&
          val.pence1Denominations2.length <= 7 &&
          val.pence1Denominations2.length >= 2 &&
          !arraysHaveSameContentsUnordered(val.pence1Denominations2, val.pence1Denominations)
      ));

      // generate pence2, this is pence1 add a coin
      ({ pence2, pence2Denominations } = rejectionSample(
        () => {
          const addCoin =
            variant === 'pence'
              ? getRandomFromArray([1, 2, 5, 10, 20, 50] as const)
              : getRandomFromArray([100, 200, 500, 1000, 2000] as const);
          return {
            pence2: pence1 + addCoin,
            pence2Denominations: [...pence1Denominations, addCoin]
          };
        },
        val => (variant === 'pence' ? val.pence2 < 100 : val.pence2 < 3000)
      ));

      // generate pence3, this is pence1 sub a coin
      const subCoinIndex = randomIntegerInclusive(0, pence1Denominations.length - 1);
      pence3Denominations = pence1Denominations.filter((_, i) => i !== subCoinIndex);
      pence3 = sumNumberArray(pence3Denominations);

      // generate pence4, this is pence1 with a difference arrangement add/sub a coin
      ({ pence4, pence4Denominations } = rejectionSample(
        () => {
          const pence4Denominations = shuffle(pence1Denominations2);
          const isAdd = getRandomBoolean() && !(pence1Denominations2.length > 6);
          if (isAdd) {
            const addCoin =
              variant === 'pence'
                ? getRandomFromArray([1, 2, 5, 10, 20, 50] as const)
                : getRandomFromArray([100, 200, 500, 1000, 2000] as const);
            return {
              pence4: sumNumberArray([...pence4Denominations, addCoin]),
              pence4Denominations: [...pence4Denominations, addCoin]
            };
          } else {
            pence4Denominations.pop();
            return {
              pence4: sumNumberArray(pence4Denominations),
              pence4Denominations
            };
          }
        },
        val =>
          ![pence1, pence2, pence3].includes(val.pence4) &&
          (variant === 'pence' ? val.pence4 < 100 : val.pence4 < 3000)
      ));
    }

    const selectables = shuffle([
      { value: pence1, denominations: shuffle(pence1Denominations) },
      { value: pence2, denominations: shuffle(pence2Denominations) },
      { value: pence3, denominations: shuffle(pence3Denominations) },
      { value: pence4, denominations: shuffle(pence4Denominations) }
    ]);

    return { selectables, isGreatest };
  },
  Component: props => {
    const {
      question: { selectables, isGreatest },
      translate,
      displayMode
    } = props;

    const flattenedMoneyArray = selectables
      .map(selectable => selectable.denominations.map(denom => moneyPenceToString[denom]))
      .flat();

    // We need to get moneyScales here, so we can use a uniform scale across money in both the items and statements:
    const moneyScales = moneyScaledSizes(flattenedMoneyArray);

    const baseSize = displayMode === 'digital' ? 80 : 100;

    return (
      <QF11SelectImagesUpTo4
        title={
          isGreatest
            ? translate.ks1Instructions.selectTheGreatestAmountOfMoney()
            : translate.ks1Instructions.selectTheSmallestAmountOfMoney()
        }
        pdfTitle={
          isGreatest
            ? translate.ks1PDFInstructions.tickTheGreatestAmountOfMoney()
            : translate.ks1PDFInstructions.tickTheSmallestAmountOfMoney()
        }
        testCorrect={[
          isGreatest
            ? Math.max(...selectables.map(({ value }) => value))
            : Math.min(...selectables.map(({ value }) => value))
        ]}
        numItems={4}
        renderItems={({ dimens }) =>
          selectables.map(({ value, denominations }) => {
            return {
              value: value,
              component: (
                <View
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    width: dimens.width * 0.95,
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  {denominations
                    .map(denom => moneyPenceToString[denom])
                    .map(denom => {
                      return displayMoney(
                        [denom],
                        baseSize * moneyScales[denom],
                        baseSize * moneyScales[denom]
                      );
                    })}
                </View>
              )
            };
          })
        }
      />
    );
  }
});

const Question3 = newQuestionContent({
  uid: 'biq',
  description: 'biq',
  keywords: [
    'Count',
    'Coins',
    'Greater than',
    'Less than',
    'Equal to',
    'Compare',
    'Pounds',
    'Notes',
    'Coins'
  ],
  schema: z
    .object({
      penceA: z
        .number()
        .int()
        .min(101)
        .max(1999)
        .refine(money => money % 100 !== 0, 'money should have pence value'),
      penceB: z
        .number()
        .int()
        .min(101)
        .max(1999)
        .refine(money => money % 100 !== 0, 'money should have pence value')
    })
    .refine(val => val.penceA !== val.penceB, 'penceA cannot be equal to penceB'),
  simpleGenerator: () => {
    const variation = getRandomFromArray([
      'differentPoundsSamePence',
      'samePoundsDifferentPence',
      'differentPoundsAndPence'
    ] as const);

    const penceA = randomIntegerInclusive(101, 1999, { constraint: x => x % 100 !== 0 });

    const penceB = randomIntegerInclusive(101, 1999, {
      constraint: x => {
        const poundsA = Math.floor(penceA / 100);
        const penceAb = penceA % 100;

        const poundsB = Math.floor(x / 100);
        const penceBb = x % 100;

        if (penceBb === 0) {
          return false;
        }

        if (variation === 'differentPoundsSamePence') {
          return poundsA !== poundsB && penceAb === penceBb;
        } else if (variation === 'samePoundsDifferentPence') {
          return poundsA === poundsB && penceAb !== penceBb;
        } else {
          return poundsA !== poundsB && penceAb !== penceBb;
        }
      }
    });

    return { penceA, penceB };
  },
  Component: ({ question, translate, displayMode }) => {
    const { penceA, penceB } = question;

    const poundsAndPenceA = penceToPoundsAndPence(penceA);
    const poundsAndPenceB = penceToPoundsAndPence(penceB);

    const statements = [
      {
        lhs: translate.answerSentences.poundsXAndYP(poundsAndPenceA.pounds, poundsAndPenceA.pence),
        rhs: translate.answerSentences.poundsXAndYP(poundsAndPenceB.pounds, poundsAndPenceB.pence),
        answer: lessThanGreaterThanOrEqualTo(penceA, penceB)
      }
    ];

    return (
      <QF6DragMatchStatements
        title={translate.ks1Instructions.dragACardToCompareTheAmounts()}
        pdfTitle={translate.ks1PDFInstructions.writeLessThanGreaterThanOrEqualSymbolsToCompareTheAmounts()}
        itemVariant="square"
        pdfLayout="itemsHidden"
        statements={statements.map(({ lhs, rhs, answer }) => ({
          lhsComponent: (
            <Text
              variant="WRN400"
              style={{ width: displayMode === 'digital' ? 250 : 400, textAlign: 'right' }}
            >
              {lhs}
            </Text>
          ),
          rhsComponent: (
            <Text
              variant="WRN400"
              style={{ width: displayMode === 'digital' ? 250 : 400, textAlign: 'left' }}
            >
              {rhs}
            </Text>
          ),
          correctAnswer: answer
        }))}
        statementStyle={{ justifyContent: 'center' }}
        items={['>', '<', '=']}
        moveOrCopy="move"
        actionPanelVariant="end"
      />
    );
  }
});

////
// Small Step
////

const SmallStep = newSmallStepContent({
  smallStep: 'CompareAmountsOfMoney',
  questionTypes: [Question1, Question2v2, Question3],
  unpublishedQuestionTypes: [Question2v2],
  archivedQuestionTypes: [Question2]
});
export default SmallStep;
